// mow-frontend/src/components/analytics/modals/AssetHistoryModal.js
// FlowSense MOW+ — Asset Lifecycle History Modal
// Version: 2.0 — Enterprise overhaul
// ─────────────────────────────────────────────────────────────────────────────
// WHAT CHANGED vs v1.0:
//   BUG FIX: Text was missing from Chakra imports — caused a runtime crash
//   PERFORMANCE: isMounted ref prevents setState after modal close
//   ENTERPRISE UI:
//   - Dark mode via useColorModeValue on all tokens
//   - Loading skeleton (3 rows) replaces bare Spinner
//   - Error state with retry CTA
//   - Search input + type-filter badge buttons (All / Corrective / Preventive / Inspection)
//   - Sortable column headers (Date, Type, Cost)
//   - Pagination — 8 rows per page with prev/next + jump input
//   - 4-stat summary row (Events, Total Cost, Avg Cost, Days Since Last)
//   - Recharts AreaChart — cost over time, toggled via Table/Chart view buttons
//   - Export CSV improved — all fields, proper quoting
//   CORE API: analyticsService.getAssetLifecycleDetails(asset.id) — UNCHANGED
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton,
  ModalFooter, VStack, HStack, Spinner, Table, Thead, Tbody, Tr, Th, Td,
  Badge, Box, Flex, Stat, StatLabel, StatNumber, StatHelpText, StatGroup,
  IconButton, Menu, MenuButton, MenuList, MenuItem, Tooltip, Text, Icon,
  Input, InputGroup, InputLeftElement, Button, ButtonGroup, Divider,
  Alert, AlertIcon, AlertTitle, AlertDescription, Progress, Skeleton,
  SkeletonText, useColorModeValue, SimpleGrid, Select,
} from '@chakra-ui/react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, ResponsiveContainer, Legend,
} from 'recharts';
import {
  MdDownload, MdPrint, MdMoreVert, MdSearch, MdSort,
  MdArrowUpward, MdArrowDownward, MdUnfoldMore, MdRefresh,
  MdViewList, MdShowChart, MdChevronLeft, MdChevronRight,
  MdTimer, MdBuild, MdCheckCircle, MdWarning,
} from 'react-icons/md';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import analyticsService from '../../../services/analyticsService';

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────

const PAGE_SIZE = 8;

const TYPE_CONFIG = {
  corrective:  { color: 'red',    icon: MdWarning,      label: 'Corrective'  },
  preventive:  { color: 'blue',   icon: MdBuild,        label: 'Preventive'  },
  inspection:  { color: 'green',  icon: MdCheckCircle,  label: 'Inspection'  },
  emergency:   { color: 'orange', icon: MdWarning,      label: 'Emergency'   },
};

const getTypeConfig = (type) =>
  TYPE_CONFIG[type?.toLowerCase()] || { color: 'gray', icon: MdBuild, label: type || 'Unknown' };

// ─────────────────────────────────────────────────────────────────────────────
// CUSTOM TOOLTIP
// ─────────────────────────────────────────────────────────────────────────────

const ChartTooltip = ({ active, payload, label, cardBg, borderColor }) => {
  if (!active || !payload?.length) return null;
  return (
    <Box bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="md" px={3} py={2} boxShadow="lg" fontSize="xs">
      <Text fontWeight="bold" mb={1}>{label}</Text>
      {payload.map((entry, i) => (
        <Flex key={i} align="center" gap={2}>
          <Box w={2} h={2} borderRadius="full" bg={entry.color} />
          <Text color="gray.500">{entry.name}:</Text>
          <Text fontWeight="semibold">${Number(entry.value).toFixed(2)}</Text>
        </Flex>
      ))}
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// SORTABLE HEADER
// ─────────────────────────────────────────────────────────────────────────────

const SortableTh = React.memo(({ label, field, sortConfig, onSort, isNumeric }) => {
  const activeColor = useColorModeValue('blue.600', 'blue.300');
  const mutedColor  = useColorModeValue('gray.400', 'gray.500');
  const isActive    = sortConfig.field === field;

  return (
    <Th
      isNumeric={isNumeric}
      cursor="pointer"
      userSelect="none"
      onClick={() => onSort(field)}
      color={isActive ? activeColor : undefined}
      _hover={{ color: activeColor }}
      whiteSpace="nowrap"
    >
      <HStack spacing={1} justify={isNumeric ? 'flex-end' : 'flex-start'} display="inline-flex">
        <Text>{label}</Text>
        {isActive
          ? (sortConfig.dir === 'asc'
              ? <Icon as={MdArrowUpward}   boxSize={3} color={activeColor} />
              : <Icon as={MdArrowDownward} boxSize={3} color={activeColor} />)
          : <Icon as={MdUnfoldMore} boxSize={3} color={mutedColor} />
        }
      </HStack>
    </Th>
  );
});
SortableTh.displayName = 'SortableTh';

// ─────────────────────────────────────────────────────────────────────────────
// LOADING SKELETON
// ─────────────────────────────────────────────────────────────────────────────

const HistorySkeleton = () => (
  <VStack align="stretch" spacing={4}>
    <SimpleGrid columns={4} spacing={3}>
      {[1,2,3,4].map(i => <Skeleton key={i} h="72px" borderRadius="md" />)}
    </SimpleGrid>
    <Skeleton h="36px" borderRadius="md" />
    {[1,2,3,4,5].map(i => (
      <HStack key={i} spacing={4} px={3}>
        <Skeleton h="4" w="80px" />
        <Skeleton h="4" w="60px" />
        <Skeleton h="4" w="70px" />
        <Skeleton h="4" flex="1" />
      </HStack>
    ))}
  </VStack>
);

// ─────────────────────────────────────────────────────────────────────────────
// MAIN MODAL
// ─────────────────────────────────────────────────────────────────────────────

const AssetHistoryModal = ({ isOpen, onClose, asset }) => {

  // ── Theme ──────────────────────────────────────────────────────────────────
  const cardBg       = useColorModeValue('white',   'gray.800');
  const statBg       = useColorModeValue('gray.50', 'gray.700');
  const borderColor  = useColorModeValue('gray.200','gray.600');
  const headingColor = useColorModeValue('gray.800','white');
  const mutedColor   = useColorModeValue('gray.500','gray.400');
  const tableHoverBg = useColorModeValue('gray.50', 'gray.700');
  const theadBg      = useColorModeValue('gray.50', 'gray.700');

  // ── State ──────────────────────────────────────────────────────────────────
  const [history,     setHistory]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [searchTerm,  setSearchTerm]  = useState('');
  const [typeFilter,  setTypeFilter]  = useState('all');
  const [sortConfig,  setSortConfig]  = useState({ field: 'date', dir: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode,    setViewMode]    = useState('table'); // 'table' | 'chart'
  const [jumpPage,    setJumpPage]    = useState('');

  const isMounted = useRef(true);

  // ── Fetch (🔒 CORE — API call unchanged) ──────────────────────────────────
  const fetchHistory = useCallback(async () => {
    if (!asset?.id || !isOpen) return;
    setLoading(true);
    setError(null);
    try {
      const data = await analyticsService.getAssetLifecycleDetails(asset.id);
      if (isMounted.current) {
        setHistory(data.history || []);
        setCurrentPage(1);
      }
    } catch (err) {
      if (isMounted.current) setError(err.message);
      console.error('[AssetHistoryModal] Fetch error:', err);
    } finally {
      if (isMounted.current) setLoading(false);
    }
  }, [asset?.id, isOpen]);

  useEffect(() => {
    isMounted.current = true;
    fetchHistory();
    return () => { isMounted.current = false; };
  }, [fetchHistory]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
      setTypeFilter('all');
      setSortConfig({ field: 'date', dir: 'desc' });
      setCurrentPage(1);
      setViewMode('table');
    }
  }, [isOpen]);

  // ── Sorting ────────────────────────────────────────────────────────────────
  const handleSort = useCallback((field) => {
    setSortConfig(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'desc' ? 'asc' : 'desc',
    }));
    setCurrentPage(1);
  }, []);

  // ── Distinct types for filter buttons ─────────────────────────────────────
  const availableTypes = useMemo(() =>
    ['all', ...new Set(history.map(h => h.type?.toLowerCase()).filter(Boolean))],
    [history]
  );

  // ── Filtered + sorted data ─────────────────────────────────────────────────
  const processedHistory = useMemo(() => {
    let result = [...history];

    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      result = result.filter(h =>
        h.notes?.toLowerCase().includes(lower) ||
        h.type?.toLowerCase().includes(lower)
      );
    }

    if (typeFilter !== 'all') {
      result = result.filter(h => h.type?.toLowerCase() === typeFilter);
    }

    result.sort((a, b) => {
      let aVal, bVal;
      if (sortConfig.field === 'date')  { aVal = new Date(a.date); bVal = new Date(b.date); }
      if (sortConfig.field === 'type')  { aVal = a.type || ''; bVal = b.type || ''; }
      if (sortConfig.field === 'cost')  { aVal = parseFloat(a.cost) || 0; bVal = parseFloat(b.cost) || 0; }
      if (typeof aVal === 'string')
        return sortConfig.dir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      return sortConfig.dir === 'asc' ? aVal - bVal : bVal - aVal;
    });

    return result;
  }, [history, searchTerm, typeFilter, sortConfig]);

  // ── Pagination ─────────────────────────────────────────────────────────────
  const totalPages  = Math.max(1, Math.ceil(processedHistory.length / PAGE_SIZE));
  const safePage    = Math.min(currentPage, totalPages);
  const pagedData   = processedHistory.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  // ── Summary stats ──────────────────────────────────────────────────────────
  const summary = useMemo(() => {
    if (!history.length) return null;
    const totalCost = history.reduce((s, h) => s + (parseFloat(h.cost) || 0), 0);
    const avgCost   = totalCost / history.length;
    const latest    = history.reduce((a, b) => new Date(a.date) > new Date(b.date) ? a : b, history[0]);
    const daysSince = Math.floor((Date.now() - new Date(latest.date)) / 86_400_000);
    const corrCount = history.filter(h => h.type?.toLowerCase() === 'corrective').length;
    return { totalCost, avgCost, daysSince, corrCount, total: history.length };
  }, [history]);

  // ── Chart data ─────────────────────────────────────────────────────────────
  const chartData = useMemo(() => {
    const sorted = [...history].sort((a, b) => new Date(a.date) - new Date(b.date));
    let running = 0;
    return sorted.map(h => {
      running += parseFloat(h.cost) || 0;
      return {
        date:        new Date(h.date).toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
        cost:        +(parseFloat(h.cost) || 0).toFixed(2),
        cumulativeCost: +running.toFixed(2),
      };
    });
  }, [history]);

  // ── Exports ────────────────────────────────────────────────────────────────
  const exportToPDF = useCallback(() => {
    const doc = new jsPDF();
    doc.setFontSize(14);
    doc.text(`Maintenance History: ${asset?.name}`, 14, 15);
    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleString()} | Events: ${history.length}`, 14, 22);
    doc.autoTable({
      head: [['Date', 'Type', 'Cost ($)', 'Notes']],
      body: history.map(h => [
        new Date(h.date).toLocaleDateString(),
        h.type || '',
        parseFloat(h.cost).toFixed(2),
        h.notes || '',
      ]),
      startY: 28,
      styles: { fontSize: 9 },
      headStyles: { fillColor: [66, 153, 225] },
    });
    doc.save(`history-${asset?.name}-${Date.now()}.pdf`);
  }, [asset, history]);

  const exportToCSV = useCallback(() => {
    const csv = [
      ['Date', 'Type', 'Cost ($)', 'Technician', 'Work Order', 'Notes'],
      ...history.map(h => [
        new Date(h.date).toLocaleDateString(),
        h.type || '',
        parseFloat(h.cost).toFixed(2),
        h.technician || '',
        h.work_order_id || '',
        `"${(h.notes || '').replace(/"/g, '""')}"`,
      ]),
    ].map(r => r.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `history-${asset?.name}-${Date.now()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }, [asset, history]);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="5xl" scrollBehavior="inside">
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent bg={cardBg} borderRadius="xl" mx={4}>

        {/* ── HEADER ────────────────────────────────────────────────────────── */}
        <ModalHeader borderBottomWidth="1px" borderColor={borderColor} pb={4}>
          <Flex justify="space-between" align="center">
            <VStack align="flex-start" spacing={0}>
              <Text fontSize="lg" fontWeight="bold" color={headingColor}>
                Lifecycle History
              </Text>
              <Text fontSize="sm" color={mutedColor} fontWeight="normal">
                {asset?.name} — {asset?.asset_type} · {asset?.location}
              </Text>
            </VStack>
            <HStack spacing={2}>
              {/* View toggle */}
              <ButtonGroup size="sm" isAttached variant="outline">
                <Tooltip label="Table view" hasArrow>
                  <IconButton icon={<MdViewList />} aria-label="Table" isActive={viewMode === 'table'} colorScheme={viewMode === 'table' ? 'blue' : 'gray'} onClick={() => setViewMode('table')} />
                </Tooltip>
                <Tooltip label="Cost chart" hasArrow>
                  <IconButton icon={<MdShowChart />} aria-label="Chart" isActive={viewMode === 'chart'} colorScheme={viewMode === 'chart' ? 'blue' : 'gray'} onClick={() => setViewMode('chart')} />
                </Tooltip>
              </ButtonGroup>

              {/* Export / Print */}
              <Menu>
                <Tooltip label="Export options" hasArrow>
                  <MenuButton as={IconButton} icon={<MdMoreVert />} size="sm" variant="ghost" aria-label="More options" />
                </Tooltip>
                <MenuList>
                  <MenuItem icon={<MdPrint />}    onClick={() => window.print()}>Print View</MenuItem>
                  <MenuItem icon={<MdDownload />} onClick={exportToPDF} isDisabled={!history.length}>Export PDF</MenuItem>
                  <MenuItem icon={<MdDownload />} onClick={exportToCSV} isDisabled={!history.length}>Export CSV</MenuItem>
                </MenuList>
              </Menu>

              {/* Retry */}
              <Tooltip label="Refresh" hasArrow>
                <IconButton icon={<MdRefresh />} size="sm" variant="ghost" onClick={fetchHistory} isLoading={loading} aria-label="Refresh" />
              </Tooltip>
            </HStack>
          </Flex>
        </ModalHeader>
        <ModalCloseButton top={4} right={4} />

        {/* ── BODY ──────────────────────────────────────────────────────────── */}
        <ModalBody py={5}>
          {loading ? (
            <HistorySkeleton />
          ) : error ? (
            <Alert status="error" variant="subtle" flexDirection="column" alignItems="center" textAlign="center" borderRadius="xl" py={8}>
              <AlertIcon boxSize={10} mb={2} />
              <AlertTitle mb={1}>Failed to Load History</AlertTitle>
              <AlertDescription fontSize="sm" color={mutedColor}>{error}</AlertDescription>
              <Button mt={4} size="sm" colorScheme="red" leftIcon={<MdRefresh />} onClick={fetchHistory}>Retry</Button>
            </Alert>
          ) : history.length === 0 ? (
            <Flex justify="center" align="center" h="200px" direction="column" gap={2}>
              <Icon as={MdBuild} boxSize={10} color="gray.300" />
              <Text color={mutedColor}>No maintenance history found for this asset.</Text>
            </Flex>
          ) : (
            <VStack align="stretch" spacing={5}>

              {/* ── KPI Summary ─────────────────────────────────────────────── */}
              {summary && (
                <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
                  {[
                    { label: 'Total Events',    value: summary.total,                        help: 'All lifecycle records', scheme: 'blue'   },
                    { label: 'Total Cost',      value: `$${summary.totalCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, help: 'Cumulative spend',       scheme: 'red'    },
                    { label: 'Avg Cost/Event',  value: `$${summary.avgCost.toFixed(2)}`,     help: 'Per maintenance event', scheme: 'orange' },
                    { label: 'Days Since Last', value: summary.daysSince,                    help: summary.daysSince <= 30 ? 'Recent' : 'Overdue review', scheme: summary.daysSince <= 30 ? 'green' : 'orange' },
                  ].map(({ label, value, help, scheme }) => (
                    <Stat key={label} bg={statBg} p={3} borderRadius="lg" borderWidth="1px" borderColor={borderColor}
                      transition="all 0.2s" _hover={{ borderColor: `${scheme}.300`, transform: 'translateY(-2px)' }}
                    >
                      <StatLabel fontSize="xs" color={mutedColor}>{label}</StatLabel>
                      <StatNumber fontSize="lg">
                        <Badge colorScheme={scheme} variant="subtle" fontSize="md" px={1}>{value}</Badge>
                      </StatNumber>
                      <StatHelpText fontSize="xs" mb={0}>{help}</StatHelpText>
                    </Stat>
                  ))}
                </SimpleGrid>
              )}

              {/* ── Controls ────────────────────────────────────────────────── */}
              <Flex gap={3} wrap="wrap" align="center" justify="space-between">
                <InputGroup maxW="280px" size="sm">
                  <InputLeftElement pointerEvents="none">
                    <Icon as={MdSearch} color="gray.400" />
                  </InputLeftElement>
                  <Input
                    placeholder="Search notes or type..."
                    value={searchTerm}
                    onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                    borderRadius="md"
                  />
                </InputGroup>

                <HStack spacing={1} flexWrap="wrap">
                  {availableTypes.map(type => {
                    const cfg = type === 'all' ? { color: 'gray', label: 'All' } : getTypeConfig(type);
                    return (
                      <Badge
                        key={type}
                        colorScheme={typeFilter === type ? cfg.color : 'gray'}
                        variant={typeFilter === type ? 'solid' : 'outline'}
                        cursor="pointer"
                        px={3} py={1}
                        borderRadius="full"
                        fontSize="xs"
                        textTransform="capitalize"
                        onClick={() => { setTypeFilter(type); setCurrentPage(1); }}
                        transition="all 0.15s"
                        _hover={{ opacity: 0.8 }}
                      >
                        {cfg.label}
                      </Badge>
                    );
                  })}
                </HStack>

                <Text fontSize="xs" color={mutedColor}>
                  {processedHistory.length} of {history.length} records
                </Text>
              </Flex>

              <Divider />

              {/* ── TABLE VIEW ──────────────────────────────────────────────── */}
              {viewMode === 'table' && (
                <>
                  <Box borderRadius="lg" borderWidth="1px" borderColor={borderColor} overflow="hidden">
                    <Table size="sm" variant="simple">
                      <Thead bg={theadBg}>
                        <Tr>
                          <SortableTh label="Date"  field="date" sortConfig={sortConfig} onSort={handleSort} />
                          <SortableTh label="Type"  field="type" sortConfig={sortConfig} onSort={handleSort} />
                          <SortableTh label="Cost"  field="cost" sortConfig={sortConfig} onSort={handleSort} isNumeric />
                          <Th>Technician</Th>
                          <Th>Work Order</Th>
                          <Th>Notes</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {pagedData.map((h, i) => {
                          const cfg = getTypeConfig(h.type);
                          return (
                            <Tr key={i} _hover={{ bg: tableHoverBg }} transition="background 0.1s">
                              <Td whiteSpace="nowrap" fontSize="xs">
                                {new Date(h.date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                              </Td>
                              <Td>
                                <Badge colorScheme={cfg.color} borderRadius="full" px={2} fontSize="10px" textTransform="capitalize">
                                  {cfg.label}
                                </Badge>
                              </Td>
                              <Td isNumeric fontWeight="semibold" color={parseFloat(h.cost) > 5000 ? 'red.500' : headingColor}>
                                ${parseFloat(h.cost).toFixed(2)}
                              </Td>
                              <Td fontSize="xs" color={mutedColor}>{h.technician || '—'}</Td>
                              <Td fontSize="xs" color={mutedColor}>{h.work_order_id || '—'}</Td>
                              <Td fontSize="xs" maxW="200px" noOfLines={2}>{h.notes || '—'}</Td>
                            </Tr>
                          );
                        })}
                        {pagedData.length === 0 && (
                          <Tr>
                            <Td colSpan={6} textAlign="center" py={8} color={mutedColor}>
                              No records match your filters.
                            </Td>
                          </Tr>
                        )}
                      </Tbody>
                    </Table>
                  </Box>

                  {/* ── Pagination ─────────────────────────────────────────── */}
                  {totalPages > 1 && (
                    <Flex justify="space-between" align="center" flexWrap="wrap" gap={2}>
                      <Text fontSize="xs" color={mutedColor}>
                        Page {safePage} of {totalPages} · {processedHistory.length} records
                      </Text>
                      <HStack spacing={1}>
                        <IconButton icon={<MdChevronLeft />} size="xs" variant="outline" isDisabled={safePage <= 1} onClick={() => setCurrentPage(p => p - 1)} aria-label="Prev" />
                        {[...Array(Math.min(5, totalPages))].map((_, i) => {
                          const page = Math.max(1, Math.min(totalPages - 4, safePage - 2)) + i;
                          if (page > totalPages) return null;
                          return (
                            <Button key={page} size="xs" variant={page === safePage ? 'solid' : 'outline'} colorScheme={page === safePage ? 'blue' : 'gray'} onClick={() => setCurrentPage(page)}>
                              {page}
                            </Button>
                          );
                        })}
                        <IconButton icon={<MdChevronRight />} size="xs" variant="outline" isDisabled={safePage >= totalPages} onClick={() => setCurrentPage(p => p + 1)} aria-label="Next" />
                        <HStack spacing={1}>
                          <Text fontSize="xs" color={mutedColor}>Go</Text>
                          <Input
                            size="xs" w="40px" textAlign="center"
                            value={jumpPage}
                            onChange={e => setJumpPage(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') {
                                const p = parseInt(jumpPage, 10);
                                if (!isNaN(p) && p >= 1 && p <= totalPages) setCurrentPage(p);
                                setJumpPage('');
                              }
                            }}
                            borderRadius="md"
                          />
                        </HStack>
                      </HStack>
                    </Flex>
                  )}
                </>
              )}

              {/* ── CHART VIEW ──────────────────────────────────────────────── */}
              {viewMode === 'chart' && chartData.length > 0 && (
                <Box>
                  <Text fontWeight="semibold" fontSize="sm" color={headingColor} mb={3}>
                    Cost Over Time
                  </Text>
                  <Box borderRadius="xl" borderWidth="1px" borderColor={borderColor} p={4} bg={statBg} minH="320px">
                    <ResponsiveContainer width="100%" height={280}>
                      <AreaChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                        <defs>
                          <linearGradient id="histGrad1" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%"  stopColor="#3182CE" stopOpacity={0.5} />
                            <stop offset="95%" stopColor="#3182CE" stopOpacity={0.02} />
                          </linearGradient>
                          <linearGradient id="histGrad2" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%"  stopColor="#E53E3E" stopOpacity={0.4} />
                            <stop offset="95%" stopColor="#E53E3E" stopOpacity={0.02} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={borderColor} />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                        <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} width={55} tickFormatter={v => `$${v}`} />
                        <RechartsTooltip content={<ChartTooltip cardBg={cardBg} borderColor={borderColor} />} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Area type="monotone" dataKey="cost"           stroke="#3182CE" strokeWidth={2} fillOpacity={1} fill="url(#histGrad1)" dot={{ r: 3 }} name="Event Cost"       />
                        <Area type="monotone" dataKey="cumulativeCost" stroke="#E53E3E" strokeWidth={2} fillOpacity={1} fill="url(#histGrad2)" dot={false}   name="Cumulative Cost" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </Box>
                </Box>
              )}

            </VStack>
          )}
        </ModalBody>

        {/* ── FOOTER ────────────────────────────────────────────────────────── */}
        <ModalFooter borderTopWidth="1px" borderColor={borderColor} pt={3}>
          <HStack spacing={3} w="full" justify="space-between">
            <Text fontSize="xs" color={mutedColor}>
              {summary ? `${summary.corrCount} corrective · ${summary.total - summary.corrCount} planned` : ''}
            </Text>
            <HStack spacing={2}>
              <Button size="sm" variant="outline" leftIcon={<MdDownload />} onClick={exportToCSV} isDisabled={!history.length}>
                CSV
              </Button>
              <Button size="sm" variant="outline" leftIcon={<MdDownload />} onClick={exportToPDF} isDisabled={!history.length}>
                PDF
              </Button>
              <Button size="sm" colorScheme="blue" onClick={onClose}>Close</Button>
            </HStack>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AssetHistoryModal;